// Dungeon design types and node-graph generator. See docs/07-content §7.3-7.5
// and docs/adr/0026-c7-dungeon-design.md.
//
// This file pins the shared types, probability tables, and deterministic
// generator for Slay-the-Spire-style dungeon runs. Catalogue authoring
// (templates / modifiers / keys / events / drop tables) is deferred to
// ADR 0027-0031. Sim-side integration of `DungeonBoonState` effects is
// bridged by dungeon-boon-effects.ts (Phase 5.x-A4).
import { Rng } from './rng.js';
/**
 * Parent type for each subtype. Used by structural tests and by the generator
 * to decide which parent node spawns which subtype roll.
 */
export const SUBTYPE_TO_PARENT = {
    light: 'battle',
    normal: 'battle',
    heavy: 'battle',
    elite: 'elite',
    midboss: 'elite',
    rarepack: 'elite',
    text: 'event',
    treasure: 'event',
    ambush: 'event',
    rest: 'camp',
    boon: 'camp',
    shop: 'camp',
    boss: 'boss',
};
/** Pattern for `DungeonTemplateId` validation (used by §J test DJ#9). */
export const DUNGEON_TEMPLATE_ID_PATTERN = /^dungeon\.[a-z][a-z0-9-]*\.t\d+$/;
// ---------------------------------------------------------------------------
// Default distribution / rolls (ADR 0026 §4.2 pinned values)
// ---------------------------------------------------------------------------
/** Tier-invariant player-facing distribution. Pinned in §4.2.2. */
export const DEFAULT_NODE_DISTRIBUTION = {
    battle: 0.7,
    elite: 0.1,
    event: 0.15,
    camp: 0.05,
};
/** Pinned in §4.2.1. */
export const DEFAULT_SUBTYPE_ROLLS = [
    {
        tierMin: 1,
        tierMax: 4,
        battle: { light: 0.5, normal: 0.35, heavy: 0.15 },
        elite: { elite: 1.0, midboss: 0.0, rarepack: 0.0 },
    },
    {
        tierMin: 5,
        tierMax: 9,
        battle: { light: 0.3, normal: 0.45, heavy: 0.25 },
        elite: { elite: 0.6, midboss: 0.32, rarepack: 0.08 },
    },
    {
        tierMin: 10,
        tierMax: 19,
        battle: { light: 0.15, normal: 0.5, heavy: 0.35 },
        elite: { elite: 0.5, midboss: 0.3, rarepack: 0.2 },
    },
    {
        tierMin: 20,
        tierMax: Infinity,
        battle: { light: 0.15, normal: 0.5, heavy: 0.35 },
        elite: { elite: 0.4, midboss: 0.3, rarepack: 0.3 },
    },
];
/** Pinned in §4.2.3 for non-fixed camp floors. */
export const DEFAULT_CAMP_SUBMENU = {
    rest: 0.6,
    boon: 0.25,
    shop: 0.15,
};
/** Pinned in §4.2.5. */
export const DEFAULT_EVENT_SUBTYPE = {
    text: 0.55,
    treasure: 0.25,
    ambush: 0.2,
};
/**
 * Standard 12-floor layout (ADR 0026 §4.2.2). Floors with fixed types/subtypes:
 *   - Floor 1: start
 *   - Floor 6: camp/shop (商人を必ず含む)
 *   - Floor 11: rest (休憩 or 宝箱、本 ADR では rest 固定)
 *   - Floor 12: boss
 * Rolled floors alternate 2/3 nodes to give the path-graph 7 combat + 3
 * non-combat shape (§7.3.2 / §7.3.4).
 */
export const STANDARD_FLOOR_LAYOUT = [
    { index: 1, nodeCount: 1, fixedNodeType: 'start' },
    { index: 2, nodeCount: 2 },
    { index: 3, nodeCount: 3 },
    { index: 4, nodeCount: 2 },
    { index: 5, nodeCount: 3 },
    { index: 6, nodeCount: 2, fixedNodeType: 'camp', fixedSubtype: 'shop' },
    { index: 7, nodeCount: 3 },
    { index: 8, nodeCount: 2 },
    { index: 9, nodeCount: 3 },
    { index: 10, nodeCount: 2 },
    { index: 11, nodeCount: 1, fixedNodeType: 'camp', fixedSubtype: 'rest' },
    { index: 12, nodeCount: 1, fixedNodeType: 'boss', fixedSubtype: 'boss' },
];
// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
/** Locate the subtype-roll table for a given Tier. Throws if no range matches. */
export function findSubtypeRollTier(rolls, tier) {
    for (const r of rolls) {
        if (tier >= r.tierMin && tier <= r.tierMax) {
            return r;
        }
    }
    throw new Error(`no SubtypeRollTier covers tier=${tier}`);
}
/** Pick a key from a weighted distribution using an Rng draw. Sum must be 1. */
function pickWeighted(rng, weights, keys) {
    const roll = rng.nextFloat();
    let acc = 0;
    for (const k of keys) {
        acc += weights[k];
        if (roll < acc) {
            return k;
        }
    }
    // Floating-point slack: fall through to last key.
    return keys[keys.length - 1];
}
// ---------------------------------------------------------------------------
// Generator (ADR 0026 §4.2.7)
// ---------------------------------------------------------------------------
const MAX_REROLL_ATTEMPTS = 16;
const NODE_TYPE_KEYS = ['battle', 'elite', 'event', 'camp'];
/**
 * Deterministic dungeon-map generator.
 *
 * Inputs: a `DungeonTemplate` and a numeric `seed`. Outputs a `DungeonMap`
 * satisfying:
 *   - Floor 1/6/11/12 are fixed per template.layout.
 *   - Rollable floors draw node types from `template.distribution`.
 *   - Every Floor-1-to-Floor-12 path passes through ≥ 2 non-combat nodes
 *     (event or camp; ambush events count by node-type per §4.2.5).
 *   - If a floor has multiple nodes, the parent-type set is not a singleton
 *     (branching difficulty differential).
 *   - On reroll exhaustion, the generator falls back by minimally injecting
 *     non-combat nodes to satisfy the path constraint.
 */
export function generateDungeonMap(template, seed) {
    if (template.layout.length !== 12) {
        throw new Error(`template.layout must have 12 floors, got ${template.layout.length}`);
    }
    const rng = Rng.fromSeed(seed);
    let lastAttempt = null;
    for (let attempt = 0; attempt < MAX_REROLL_ATTEMPTS; attempt++) {
        const candidate = generateOnce(template, rng);
        lastAttempt = candidate;
        if (satisfiesConstraints(candidate, template)) {
            return materialise(template, seed, candidate);
        }
    }
    const fallback = forceConstraintSatisfaction(template, lastAttempt);
    return materialise(template, seed, fallback);
}
function generateOnce(template, rng) {
    const floors = [];
    for (const floor of template.layout) {
        const types = [];
        for (let i = 0; i < floor.nodeCount; i++) {
            types.push(floor.fixedNodeType ?? pickNodeType(rng, template.distribution));
        }
        // Per-floor differential: if a rollable floor has ≥ 2 nodes that all came
        // up the same type, swap the last one to a different type drawn from the
        // distribution (excluding the duplicated type) so the branching always
        // offers difficulty contrast.
        if (!floor.fixedNodeType && floor.nodeCount >= 2) {
            const allSame = types.every((t) => t === types[0]);
            if (allSame) {
                types[types.length - 1] = pickDifferentType(rng, template.distribution, types[0]);
            }
        }
        const nodes = types.map((type, i) => floor.fixedSubtype
            ? { floorIndex: floor.index, nodeIndex: i, type, subtype: floor.fixedSubtype }
            : { floorIndex: floor.index, nodeIndex: i, type });
        floors.push(nodes);
    }
    const edges = buildEdges(floors, rng);
    return { floors, edges };
}
function pickDifferentType(rng, dist, exclude) {
    const remaining = NODE_TYPE_KEYS.filter((k) => k !== exclude);
    const remainingWeight = remaining.reduce((a, k) => a + dist[k], 0);
    if (remainingWeight <= 0 || remaining.length === 0) {
        return remaining[0] ?? 'event';
    }
    const reweighted = {
        battle: 0,
        elite: 0,
        event: 0,
        camp: 0,
    };
    for (const k of remaining) {
        reweighted[k] = dist[k] / remainingWeight;
    }
    return pickWeighted(rng, reweighted, remaining);
}
function pickNodeType(rng, dist) {
    return pickWeighted(rng, dist, NODE_TYPE_KEYS);
}
function buildEdges(floors, rng) {
    const edges = [];
    for (let f = 0; f < floors.length - 1; f++) {
        const here = floors[f];
        const next = floors[f + 1];
        for (let i = 0; i < here.length; i++) {
            // Each node connects to 1-2 next-floor nodes. Prefer the same index
            // and an adjacent one to keep edges non-crossing in the typical layout.
            const target0 = Math.min(i, next.length - 1);
            const altOffset = rng.nextBool() ? -1 : 1;
            const target1 = Math.max(0, Math.min(next.length - 1, target0 + altOffset));
            const targets = new Set([target0, target1]);
            for (const t of targets) {
                edges.push({ from: [f + 1, i], to: [f + 2, t] });
            }
        }
    }
    return edges;
}
/**
 * Constraint check: every full path has ≥ 2 non-combat (event or camp) nodes,
 * and every rollable floor with ≥ 2 nodes has at least 2 distinct parent types
 * (so branching always offers difficulty contrast). Floors with a fixed type
 * (Floor 1/6/11/12 in the standard layout) are exempt from the uniformity
 * rule by design — Floor 6's two shop nodes guarantee the non-combat budget.
 */
function satisfiesConstraints(attempt, template) {
    if (!pathHasNonCombatTwo(attempt)) {
        return false;
    }
    for (let f = 0; f < attempt.floors.length; f++) {
        const floor = attempt.floors[f];
        const constraint = template.layout[f];
        if (constraint.fixedNodeType)
            continue;
        if (floor.length >= 2) {
            const types = new Set(floor.map((n) => n.type));
            if (types.size === 1) {
                return false;
            }
        }
    }
    return true;
}
function pathHasNonCombatTwo(attempt) {
    const { floors, edges } = attempt;
    const adj = new Map();
    for (const e of edges) {
        const fk = `${e.from[0]},${e.from[1]}`;
        const tk = `${e.to[0]},${e.to[1]}`;
        if (!adj.has(fk))
            adj.set(fk, []);
        adj.get(fk).push(tk);
    }
    const isNonCombat = (n) => n.type === 'event' || n.type === 'camp';
    const nodeAt = (key) => {
        const [f, i] = key.split(',').map(Number);
        return floors[f - 1][i];
    };
    // DFS each path from Floor 1 to Floor 12, counting non-combat nodes.
    const root = `1,0`;
    const stack = [
        { key: root, nonCombat: isNonCombat(nodeAt(root)) ? 1 : 0 },
    ];
    while (stack.length > 0) {
        const { key, nonCombat } = stack.pop();
        const next = adj.get(key);
        if (!next || next.length === 0) {
            // Leaf (Floor 12). All paths must satisfy.
            const [f] = key.split(',').map(Number);
            if (f === 12 && nonCombat < 2) {
                return false;
            }
            continue;
        }
        for (const child of next) {
            const add = isNonCombat(nodeAt(child)) ? 1 : 0;
            stack.push({ key: child, nonCombat: nonCombat + add });
        }
    }
    return true;
}
/**
 * Fallback: minimally inject non-combat nodes by promoting battle/elite nodes
 * on early rolled floors until the path constraint holds. After path repair,
 * a second pass repairs floor-uniformity (no all-same row of ≥ 2 nodes) by
 * swapping one battle node to a different non-combat type. Both passes pick
 * conversion targets that avoid duplicating an existing type on the same
 * floor, so the floor-diversity invariant is preserved.
 */
function forceConstraintSatisfaction(template, last) {
    const floors = last.floors.map((f) => f.map((n) => ({ ...n })));
    // Pass 1: ensure every full path has ≥ 2 non-combat nodes.
    outer: for (const floor of template.layout) {
        if (floor.fixedNodeType)
            continue;
        for (let i = 0; i < floors[floor.index - 1].length; i++) {
            if (pathHasNonCombatTwo({ floors, edges: last.edges })) {
                break outer;
            }
            const node = floors[floor.index - 1][i];
            if (node.type === 'battle' || node.type === 'elite') {
                const target = pickNonCombatAvoidingFloor(floors[floor.index - 1], i);
                floors[floor.index - 1][i] = { ...node, type: target };
            }
        }
    }
    // Pass 2: repair floor-uniformity. For any rollable floor with all-same
    // types, change the last node to a different type while preserving the
    // path constraint (we pick a non-combat alternative — never reduces the
    // non-combat count, so the constraint stays satisfied).
    for (const floor of template.layout) {
        if (floor.fixedNodeType)
            continue;
        const row = floors[floor.index - 1];
        if (row.length < 2)
            continue;
        const types = new Set(row.map((n) => n.type));
        if (types.size === 1) {
            const last = row[row.length - 1];
            const target = pickAlternativeType(last.type);
            row[row.length - 1] = { ...last, type: target };
        }
    }
    return { floors, edges: last.edges };
}
function pickNonCombatAvoidingFloor(row, excludeIndex) {
    const occupied = new Set(row.filter((_, i) => i !== excludeIndex).map((n) => n.type));
    if (!occupied.has('event'))
        return 'event';
    if (!occupied.has('camp'))
        return 'camp';
    return 'event';
}
function pickAlternativeType(current) {
    switch (current) {
        case 'battle':
            return 'event';
        case 'elite':
            return 'event';
        case 'event':
            return 'camp';
        case 'camp':
            return 'event';
        default:
            return 'event';
    }
}
function materialise(template, seed, attempt) {
    return {
        templateId: template.id,
        seed,
        floors: attempt.floors.map((f) => f.map((n) => ({ ...n }))),
        edges: attempt.edges.map((e) => ({ ...e })),
    };
}
//# sourceMappingURL=dungeon.js.map